import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import { toast } from 'react-hot-toast';
import { db, storage } from '@root/firebase'; // Import your Firebase config
import Modal from '@/components/Modal';
import {
  Timestamp,
  addDoc,
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  updateDoc,
  deleteField,
  deleteDoc,
} from 'firebase/firestore';
import {
  deleteObject,
  getMetadata,
  ref as storageRef,
  listAll,
} from 'firebase/storage';
import DocUploader from '@components/DocUploader';
import TrashButton from '@components/TrashButton';
import { v4 as uuidv4 } from 'uuid';

import { formatDate } from '@utils/Date';

import Tour from '@components/Tour';
import { smartDogTourSteps } from '@utils/TourSteps/Dogs';

function Dog() {
  const { globalUser, globalData, LogError } = useAuth(); // Access the user's UID
  const [dogLibraries, setDogLibraries] = useState([]); // Dog folders
  const [activeAccordion, setActiveAccordion] = useState(null); // Active accordion
  const [newSubfolder, setNewSubfolder] = useState(''); // New subfolder input
  const [selectedDog, setSelectedDog] = useState(''); // Selected dog folder - Dog Document Id
  const [selectedDogName, setSelectedDogName] = useState(''); // Selected dog folder - Dog Name
  const [subFolders, setSubFolders] = useState([]); // Subfolders of the selected dog folder
  const [selectedSubFolder, setSelectedSubFolder] = useState(''); // Selected subfolder
  const [fileName, setFileName] = useState(''); // File name input
  const [fileDescription, setFileDescription] = useState(''); // File description input
  const [masterFileList, setMasterFileList] = useState([]); // Complete list of all files
  const [fileList, setFileList] = useState([]); // Files to display
  const [currentPage, setCurrentPage] = useState(0); // Current page for pagination
  const [isLoading, setIsLoading] = useState(false); // Loading state for files
  const [labelFileList, setLabelFileList] = useState('Recent Files'); // Loading state for files
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [documentId, setDocumentId] = useState(''); // File description input
  const [subFolderName, setSubFolderName] = useState(''); // File description input

  const [deleteFileName, setDeleteFileName] = useState('');
  const [deleteFileObj, setDeleteFileObj] = useState('');

  const docUploaderRef = useRef(); // Ref for DocUploader

  const resultsPerPage = 10; // Number of files per page
  const startIndex = currentPage * resultsPerPage;
  const currentFiles = fileList.slice(startIndex, startIndex + resultsPerPage);
  const totalPages = Math.ceil(fileList.length / resultsPerPage);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [isLoadingDogs, setIsLoadingDogs] = useState(true);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // --- counts ---
  const getDogFileCount = (dog) =>
    dog?.subfolders?.reduce((acc, sf) => acc + (sf.files?.length || 0), 0) || 0;

  const getSubfolderFileCount = (subfolder) => subfolder?.files?.length || 0;

  //  *************************************************************************************
  //  GET and FETCH all Dog folder, sub folders and files
  //  *************************************************************************************
  useEffect(() => {
    const fetchDogLibraries = async () => {
      setIsLoadingDogs(true);
      try {
        const userDogsCollectionRef = collection(
          db,
          `users/${globalUser.uid}/dogs`
        );
        const querySnapshot = await getDocs(userDogsCollectionRef);

        let recentFiles = [];
        let formattedDogs = [];

        for (const dogDoc of querySnapshot.docs) {
          const dogData = dogDoc.data();
          const documentId = dogDoc.id;
          const dogName = dogData.dogName || 'Unnamed Dog';

          console.log(`🐶 Dog: ${dogName} (ID: ${documentId})`);

          // Step 2: Fetch subfolders
          const subfoldersCollectionRef = collection(
            db,
            `users/${globalUser.uid}/dogs/${documentId}/subfolders`
          );
          const subfolderSnapshot = await getDocs(subfoldersCollectionRef);

          let subfolders = [];

          for (const subfolderDoc of subfolderSnapshot.docs) {
            const subfolderName = subfolderDoc.id;
            if (!subfolderName) continue;

            // Step 3: Fetch files
            const filesCollectionRef = collection(
              db,
              `users/${globalUser.uid}/dogs/${documentId}/subfolders/${subfolderName}/files`
            );
            const filesSnapshot = await getDocs(filesCollectionRef);

            let filesInSubfolder = [];

            filesSnapshot.forEach((fileDoc) => {
              const fileData = fileDoc.data();

              filesInSubfolder.push({
                documentId,
                dogName,
                subfolder: subfolderName,
                fileName: fileDoc.id,
                file_title: fileData.file_title || 'Untitled',
                file_size: fileData.file_size || 'Unknown Size',
                file_url: fileData.file_url || '',
                upload_date: fileData.upload_date || null,
              });
            });

            subfolders.push({
              subfolderName,
              files: filesInSubfolder,
            });

            recentFiles = [...recentFiles, ...filesInSubfolder];
          }

          formattedDogs.push({
            documentId,
            dogName,
            subfolders,
            file_name: dogData.file_name || 'Unknown',
            file_size: dogData.file_size || 'Unknown Size',
            file_title: dogData.file_title || 'Untitled File',
            ...dogData,
          });
        }

        setDogLibraries(formattedDogs);
        setFileList(recentFiles);
        setMasterFileList(recentFiles);
      } catch (error) {
        console.error('Error fetching dog folders:', error);
        toast.error('Failed to load dog folders.');
      } finally {
        setIsLoadingDogs(false);
      }
    };

    if (globalUser?.uid) {
      fetchDogLibraries();
    }
  }, [globalUser?.uid]);

  const resetFileList = () => {
    setFileList(masterFileList); // Reset to the master list
    setCurrentPage(0); // Reset pagination
  };

  //  *************************************************************************************
  //  Update subfolders when a dog folder is selected
  //  *************************************************************************************
  useEffect(() => {
    if (selectedDog) {
      // Find the selected dog object
      const selectedDogObj = dogLibraries.find(
        (dog) => dog.documentId === selectedDog
      );

      // Ensure we extract the correct subfolder names
      if (selectedDogObj && selectedDogObj.subfolders.length > 0) {
        setSubFolders(
          selectedDogObj.subfolders.map((subfolder) => subfolder.subfolderName)
        ); // ✅ Correct
      } else {
        setSubFolders([]); // Reset if no subfolders are found
      }
    } else {
      setSubFolders([]); // Reset when no dog is selected
    }
  }, [selectedDog, dogLibraries]);

  //  *************************************************************************************
  //  Handle subfolder click to show specific files
  //  *************************************************************************************
  const handleSubfolderClick = (documentId, subfolderName) => {
    setIsLoading(true); // Show loading state

    try {
      setSelectedSubFolder(subfolderName); // Highlight the selected subfolder

      const selectedDog = dogLibraries.find(
        (dog) => dog.documentId === documentId
      );

      if (!selectedDog) {
        toast.error('Dog folder not found.');
        return;
      }

      setLabelFileList(
        `Folder Files: ${selectedDog.dogName} -> ${subfolderName}`
      );

      // Filter files in the masterFileList that belong to the selected documentId and subfolder
      const associatedFiles = masterFileList.filter(
        (file) =>
          file.documentId === documentId &&
          file.subfolder === subfolderName.trim()
      );

      // Update the file list with the associated files
      setFileList(associatedFiles);
      setCurrentPage(0); // Reset pagination to the first page

      if (associatedFiles.length === 0) {
        // Replace toast.info with a default toast notification
        toast('No files found in the selected subfolder.', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Error displaying files for subfolder:', error);
      LogError('Error', {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack || 'No stack trace available',
          name: error.name || 'Error',
        },
        userName: globalData.name || 'No Name',
        uid: globalUser.uid || 'No ID',
        functionCode: 'handleSubfolderClick',
        page: 'SMART DOG',
      });
      toast.error('Failed to display files for the selected subfolder.');
    } finally {
      setIsLoading(false); // Hide loading state
    }
  };

  //  *************************************************************************************
  //  UPLOAD FILE for user's Dog
  //  *************************************************************************************
  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    const selectedFile = docUploaderRef.current?.getSelectedFile();

    if (!selectedDog) {
      toast.error('Please select a dog folder.');
      return;
    }

    if (!selectedSubFolder) {
      toast.error('Please select a subfolder.');
      return;
    }

    if (!fileName.trim()) {
      toast.error('File name cannot be empty.');
      return;
    }

    if (!fileDescription.trim()) {
      toast.error('Please provide a file description.');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file to upload.');
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    try {
      const uploadPath = `users/${
        globalUser.uid
      }/dogs/${selectedDog}/smartfiles/${selectedSubFolder}/${fileName.trim()}`;
      const { downloadURL, fileSizeInMB } =
        await docUploaderRef.current.handleUpload(uploadPath, (progress) =>
          setUploadProgress(progress)
        );

      const newFileData = {
        file_title: fileName,
        file_description: fileDescription,
        file_name: selectedFile.name,
        file_url: downloadURL,
        file_size: fileSizeInMB,
        upload_date: Timestamp.now(),
      };

      const fileDocRef = doc(
        collection(
          db,
          `users/${globalUser.uid}/dogs/${selectedDog}/subfolders/${selectedSubFolder}/files`
        ),
        fileName
      );

      await setDoc(fileDocRef, newFileData);

      const newFileEntry = {
        documentId: selectedDog,
        dogName: selectedDogName,
        subfolder: selectedSubFolder,
        fileName,
        ...newFileData,
      };

      setMasterFileList((prev) => [newFileEntry, ...prev]);
      setFileList((prev) => [newFileEntry, ...prev]);

      toast.success('File uploaded successfully!');

      setFileName('');
      setFileDescription('');
      setSelectedDog('');
      setSelectedSubFolder('');
    } catch (error) {
      console.error('Error Adding Record:', error);
      LogError('Error', {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack || 'No stack trace available',
          name: error.name || 'Error',
        },
        userName: globalData.name || 'No Name',
        uid: globalUser.uid || 'No ID',
        functionCode: 'handleSubmit',
        page: 'SMART DOG',
      });

      toast.error('Failed to add record. Check the console for details.');
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  //  *************************************************************************************
  //  CREATE a new sub folder for user's Dog
  //  *************************************************************************************
  const addSubfolder = async (documentId) => {
    if (!newSubfolder.trim()) {
      toast.error('Subfolder name cannot be empty.');
      return;
    }

    try {
      // Reference the specific dog's document in the `dogs` collection
      const subfolderRef = doc(
        collection(db, `users/${globalUser.uid}/dogs/${documentId}/subfolders`),
        newSubfolder
      );

      // Create the subfolder document in Firestore
      await setDoc(subfolderRef, {
        createdAt: Timestamp.now(), // Optional: Store creation date
      });

      toast.success('Subfolder added successfully!');
      setNewSubfolder(''); // Clear the input field

      // Refresh local state and update UI
      // Immediately update local state to reflect changes in UI
      setDogLibraries((prev) =>
        prev.map((dog) =>
          dog.documentId === documentId
            ? {
                ...dog,
                subfolders: [
                  ...dog.subfolders,
                  { subfolderName: newSubfolder, files: [] }, // ✅ Correct
                ],
              }
            : dog
        )
      );
    } catch (error) {
      console.error('Error adding subfolder:', error);
      LogError('Error', {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack || 'No stack trace available',
          name: error.name || 'Error',
        },
        userName: globalData.name || 'No Name',
        uid: globalUser.uid || 'No ID',
        functionCode: 'addSubfolder',
        page: 'SMART DOG',
      });
      toast.error('Failed to add subfolder.');
    }
  };

  //  *************************************************************************************
  //  DELETE sub folder for user's Dog
  //  *************************************************************************************
  const deleteSubfolder = async (documentId, subfolderName) => {
    try {
      // Step 1: Reference the specific subfolder document in Firestore
      const subfolderRef = doc(
        db,
        `users/${globalUser.uid}/dogs/${documentId}/subfolders/${subfolderName}`
      );

      // Step 2: Fetch all files inside the subfolder
      const filesCollectionRef = collection(
        db,
        `users/${globalUser.uid}/dogs/${documentId}/subfolders/${subfolderName}/files`
      );
      const filesSnapshot = await getDocs(filesCollectionRef);

      if (!filesSnapshot.empty) {
        // Step 3: Delete each file from Firebase Storage and Firestore
        const deleteStoragePromises = filesSnapshot.docs.map(
          async (fileDoc) => {
            const fileData = fileDoc.data();
            const filePath = fileData.file_url; // Ensure file_url exists

            if (filePath) {
              try {
                const fileRef = storageRef(storage, filePath);
                await deleteObject(fileRef); // Delete from Firebase Storage
                console.log(`Deleted from Storage: ${filePath}`);
              } catch (storageError) {
                console.error(
                  `Failed to delete from Storage: ${filePath}`,
                  storageError
                );
              }
            }

            // Delete file document from Firestore
            await deleteDoc(doc(filesCollectionRef, fileDoc.id));
            console.log(`Deleted from Firestore: ${fileDoc.id}`);
          }
        );

        // Wait for all deletions to complete
        await Promise.all(deleteStoragePromises);
      }

      // Step 4: Delete the subfolder document itself
      await deleteDoc(subfolderRef);
      console.log(`Deleted Subfolder Document: ${subfolderRef.path}`);

      // Step 5: Update local state (UI) to remove the subfolder immediately
      setDogLibraries((prev) =>
        prev.map((dog) =>
          dog.documentId === documentId
            ? {
                ...dog,
                subfolders: dog.subfolders.filter(
                  (subfolder) => subfolder.subfolderName !== subfolderName
                ),
              }
            : dog
        )
      );

      // Step 6: Remove deleted files from local lists
      const updatedMasterFileList = masterFileList.filter(
        (file) =>
          !(file.documentId === documentId && file.subfolder === subfolderName)
      );
      setMasterFileList(updatedMasterFileList);

      const updatedFileList = fileList.filter(
        (file) =>
          !(file.documentId === documentId && file.subfolder === subfolderName)
      );
      setFileList(updatedFileList);

      toast.success('Subfolder and all files deleted successfully!');
    } catch (error) {
      console.error('Error deleting subfolder:', error);
      LogError('Error', {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack || 'No stack trace available',
          name: error.name || 'Error',
        },
        userName: globalData.name || 'No Name',
        uid: globalUser.uid || 'No ID',
        functionCode: 'deleteSubFolder',
        page: 'SMART DOG',
      });
      toast.error('Failed to delete subfolder.');
    }
  };

  //  *************************************************************************************
  //  DELETE a dog's single file
  //  *************************************************************************************
  const handleFileDelete = async (file) => {
    try {
      // Step 1: Delete the file from Firebase Storage
      const filePath = `${file.file_url}`;
      const fileRef = storageRef(storage, filePath);
      await deleteObject(fileRef);

      // Step 2: Remove the file entry from Firestore
      const fileDocRef = doc(
        db,
        `users/${globalUser.uid}/dogs/${file.documentId}/subfolders/${file.subfolder}/files/${file.fileName}`
      );
      await deleteDoc(fileDocRef);

      // Step 3: Update the local state immediately without refreshing
      setMasterFileList((prev) =>
        prev.filter(
          (f) =>
            !(
              f.documentId === file.documentId &&
              f.subfolder === file.subfolder &&
              f.fileName === file.fileName
            )
        )
      );

      setFileList((prev) =>
        prev.filter(
          (f) =>
            !(
              f.documentId === file.documentId &&
              f.subfolder === file.subfolder &&
              f.fileName === file.fileName
            )
        )
      );

      toast.success('File deleted successfully!');
    } catch (error) {
      console.error('Error deleting file:', error);
      LogError('Error', {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack || 'No stack trace available',
          name: error.name || 'Error',
        },
        userName: globalData.name || 'No Name',
        uid: globalUser.uid || 'No ID',
        functionCode: 'handleFileDelete',
        page: 'SMART DOG',
      });
      toast.error('Failed to delete the file. Please try again.');
    }
  };

  return (
    <Layout>
      <div className='row page-titles'>
        <div className='col-lg-3'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Features</li>
            <li className='breadcrumb-item'>Dog Files</li>
          </ol>
        </div>
        <div className='col-lg-4'>
          <Tour steps={smartDogTourSteps} pageKey='smart-dog' />
        </div>
      </div>

      <div className='row'>
        {/* Sidebar */}
        <div className='col-xl-4 col-lg-4'>
          <div className='col-xl-12 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent'>
                <h5 className='mb-0'>Smart Dog File Library</h5>
              </div>
              <div className='card-body'>
                <div className='custom-accordion' id='libraryAccordion'>
                  {isLoadingDogs ? (
                    <div
                      className='spinner-border text-primary m-1'
                      role='status'
                    >
                      <span className='sr-only'>Loading...</span>
                    </div>
                  ) : dogLibraries.length > 0 ? (
                    dogLibraries.map((dog) => (
                      <div key={dog.dogName}>
                        {/* Dog Folder */}
                        <a
                          className='text-body fw-medium py-1 d-flex align-items-center'
                          href={`#collapse-${dog.dogName}`}
                          role='button'
                          aria-expanded={activeAccordion === dog.dogName}
                          aria-controls={`collapse-${dog.dogName}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveAccordion((prev) =>
                              prev === dog.dogName ? null : dog.dogName
                            );
                          }}
                        >
                          <i
                            className={`mdi ${
                              activeAccordion === dog.dogName
                                ? 'mdi-folder-open'
                                : 'mdi-folder'
                            } font-size-20 text-warning me-2`}
                          ></i>
                          {dog.dogName}
                          <i
                            className={`mdi mdi-chevron-${
                              activeAccordion === dog.dogName ? 'down' : 'up'
                            } accor-down-icon ms-auto`}
                          ></i>

                          {getDogFileCount(dog) > 0 ? (
                            <span className='badge badge-xs badge-primary text-white badge-sm float-end"'>
                              {getDogFileCount(dog)}
                            </span>
                          ) : (
                            <span className='badge badge-xs badge-light text-white badge-sm float-end"'>
                              {getDogFileCount(dog)}
                            </span>
                          )}
                        </a>

                        {/* Subfolders */}
                        <div
                          className={`collapse ${
                            activeAccordion === dog.dogName ? 'show' : ''
                          }`}
                          id={`collapse-${dog.dogName}`}
                        >
                          <div className='card border-0 shadow-none ps-2 mb-0'>
                            <ul className='list-unstyled mb-1'>
                              {dog.subfolders.length > 0 ? (
                                dog.subfolders.map((subfolder) => (
                                  <li
                                    key={`${dog.dogName}-${subfolder.subfolderName}`}
                                    className='d-flex align-items-center justify-content-between'
                                  >
                                    <a
                                      href='#'
                                      className='links d-flex align-items-center'
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleSubfolderClick(
                                          dog.documentId,
                                          subfolder.subfolderName
                                        );
                                      }}
                                    >
                                      <div>
                                        <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2'></i>
                                        <small>{subfolder.subfolderName}</small>
                                      </div>

                                      {getSubfolderFileCount(subfolder) > 0 && (
                                        <span className='text-primary fst-italic small ms-1'>
                                          - {getSubfolderFileCount(subfolder)}
                                          {getSubfolderFileCount(subfolder) > 1
                                            ? ' files'
                                            : ' file'}
                                        </span>
                                      )}
                                    </a>
                                    <button
                                      className='btn btn-link p-0 ms-auto'
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setDocumentId(dog.documentId);
                                        setSubFolderName(
                                          subfolder.subfolderName
                                        );
                                      }}
                                      data-bs-toggle='modal'
                                      data-bs-target='#confirmDelete'
                                    >
                                      <i
                                        className='fa fa-trash font-12 align-middle me-2'
                                        style={{
                                          color: '#cccccc',
                                        }}
                                      ></i>
                                    </button>
                                  </li>
                                ))
                              ) : (
                                <li>
                                  <a
                                    href='#'
                                    className='links d-flex align-items-center'
                                  >
                                    <div
                                      style={{ flexGrow: 1, maxWidth: '75%' }}
                                    >
                                      <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2'></i>
                                      <small className='text-muted'>
                                        No Subfolders
                                      </small>
                                    </div>
                                  </a>
                                </li>
                              )}
                            </ul>

                            {/* Add Subfolder Input */}
                            <div className='d-flex align-items-center mt-2'>
                              <div className='col-8'>
                                <input
                                  className='form-control form-control-sm'
                                  type='text'
                                  placeholder={`Add subfolder...`}
                                  value={newSubfolder}
                                  onChange={(e) =>
                                    setNewSubfolder(e.target.value)
                                  }
                                />
                              </div>
                              <div className='col-4 ms-2'>
                                <button
                                  type='button'
                                  className='btn btn-rounded btn-primary btn-xs'
                                  onClick={() => addSubfolder(dog.documentId)}
                                >
                                  <i className='mdi mdi-check'></i> Add
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>
                      No Dogs Registered. Go to your user profile and register
                      your dog
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Results */}
        <div className='col-lg-8' id='file-listing'>
          <div className='card'>
            <div className='card-header'>
              <h5 className='mb-0'>{labelFileList}</h5>
              <button
                type='button'
                className='btn btn-primary btn-xs'
                data-bs-toggle='modal'
                data-bs-target='#uploadFile'
              >
                <i className='fa fa-upload font-12 align-middle me-2'></i>{' '}
                Upload a File
              </button>
            </div>
            <div className='card-body position-relative'>
              {isLoading ? (
                <p>Loading...</p>
              ) : fileList.length > 0 ? (
                <>
                  {/* Display Recent Files in a Table */}
                  <div className='table-responsive'>
                    <table className='table table-hover table-responsive-sm'>
                      <thead>
                        <tr>
                          <th className='fs-12 fw-bold'>#</th>
                          <th scope='col' className='fs-12 fw-bold'>
                            Dog Name
                          </th>
                          <th scope='col' className='fs-12 fw-bold'>
                            File Name
                          </th>
                          <th scope='col' className='fs-12 fw-bold'>
                            Size
                          </th>
                          <th scope='col' className='fs-12 fw-bold'>
                            Uploaded On
                          </th>
                          <th scope='col' className='fs-12 fw-bold'>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentFiles.map((file, index) => (
                          <tr key={index}>
                            <th>
                              <span className='badge badge-light'>
                                {index + 1}
                              </span>
                            </th>
                            <td>{file.dogName}</td>
                            <td>
                              <a
                                href={file.file_url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-dark fw-medium'
                              >
                                {file.file_title}
                              </a>
                            </td>
                            <td>{file.file_size} MB</td>
                            <td className='color-primary'>
                              {file.upload_date
                                ? formatDate(file.upload_date.toDate())
                                : 'Unknown'}
                            </td>
                            <td>
                              <button
                                className='btn btn-link p-0 ms-auto'
                                onClick={(e) => {
                                  e.preventDefault();
                                  setDeleteFileName(file.file_title);
                                  setDeleteFileObj(file);
                                }}
                                data-bs-toggle='modal'
                                data-bs-target='#confirmFileDelete'
                              >
                                <i
                                  className='fa fa-trash font-12 align-middle me-2'
                                  style={{
                                    color: '#cccccc',
                                  }}
                                ></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  <div className='d-flex justify-content-between mt-3'>
                    <button
                      className='btn btn-secondary btn-sm'
                      onClick={handlePreviousPage}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <button
                      className='btn btn-secondary btn-sm'
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <p>No files found.</p>
              )}
            </div>
          </div>
        </div>

        {/* File Upload */}
        <Modal
          id='uploadFile'
          title='Add a New File to Your Dog Records'
          onConfirm={async () => {
            setIsSaving(true);
            try {
              await handleSubmit(); // success => closes
            } finally {
              setIsSaving(false);
            }
          }}
          confirmText='Upload'
          loading={isSaving}
          loadingText='Uploading...'
        >
          {/* Dog Folder Selection */}
          <div className='form-floating mb-3'>
            <select
              id='dogFolder'
              className='form-select native-select'
              value={selectedDog}
              onChange={(e) => {
                const documentId = e.target.value;
                const selectedDogObj = dogLibraries.find(
                  (dog) => dog.documentId === documentId
                );

                setSelectedDog(documentId);
                setSelectedDogName(selectedDogObj?.dogName || '');
              }}
            >
              <option value=''>Select a Dog Folder</option>

              {dogLibraries.length > 0 ? (
                dogLibraries.map((dog) => (
                  <option key={dog.documentId} value={dog.documentId}>
                    {dog.dogName || 'Unnamed Dog'}
                  </option>
                ))
              ) : (
                <option disabled>No Dogs Available</option>
              )}
            </select>
            <label htmlFor='dogFolder' className='form-label'>
              Dog Folder
            </label>
          </div>

          {/* Subfolder Selection */}
          <div className='form-floating mb-3'>
            <select
              id='subFolder'
              className='form-select native-select'
              value={selectedSubFolder}
              onChange={(e) => setSelectedSubFolder(e.target.value)}
              disabled={!subFolders.length}
            >
              <option value=''>Select a Subfolder</option>
              {subFolders.map((subFolder) => (
                <option key={subFolder} value={subFolder}>
                  {subFolder}
                </option>
              ))}
            </select>
            <label htmlFor='subFolder' className='form-label'>
              Subfolder
            </label>
          </div>

          {/* File Name Input */}
          <div className='form-floating mb-3'>
            <input
              type='text'
              id='fileName'
              className='form-control'
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
            <label htmlFor='fileName' className='form-label'>
              File Name
            </label>
          </div>

          {/* File Description */}
          <div className='form-floating mb-3'>
            <textarea
              id='fileDescription'
              className='form-control'
              rows='3'
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
            ></textarea>
            <label htmlFor='fileDescription' className='form-label'>
              File Description
            </label>
          </div>

          {/* File Dropzone */}
          <div className='mb-3'>
            <label className='form-label'>Upload File</label>
            <DocUploader ref={docUploaderRef} />
          </div>
        </Modal>

        {/* Confirm Delete Dog Folder*/}
        <Modal
          id='confirmDelete'
          title='Remove This Folder?'
          onConfirm={async () => {
            setIsDeleting(true);
            try {
              await deleteSubfolder(documentId, subFolderName);
            } finally {
              setIsDeleting(false);
            }
          }}
          confirmText='Yes, delete'
          cancelText='Cancel'
          loading={isDeleting}
          loadingText='Deleting...'
          size='medium'
        >
          <p>{`Are you sure you want to delete ${subFolderName}?`}</p>
        </Modal>

        {/* Confirm Delete Dog File*/}
        <Modal
          id='confirmFileDelete'
          title='Remove This File?'
          onConfirm={async () => {
            setIsDeleting(true);
            try {
              await handleFileDelete(deleteFileObj);
            } finally {
              setIsDeleting(false);
            }
          }}
          confirmText='Yes, delete'
          cancelText='Cancel'
          loading={isDeleting}
          loadingText='Deleting...'
          size='medium'
        >
          <p>
            Are you sure you want to delete <strong>{deleteFileName}</strong>?
          </p>
        </Modal>
      </div>
    </Layout>
  );
}

export default Dog;
